import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    FEATURE_OUTSTANDING_TASKS_VIEW,
    MissionService,
    PersonsOrganisationDto,
    PreferencesService,
    hasFeatureFlag
} from '@flyfreely-portal-ui/flyfreely';
import {
    ColumnSortPreferences,
    TableColumn,
    TableConfig
} from '@flyfreely-portal-ui/flyfreely-table';
import { FormatDateTimePipe } from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import {
    collapseOnLeaveAnimation,
    expandOnEnterAnimation
} from 'angular-animations';
import { applyPreferences } from 'libs/flyfreely-table/src/lib/helpers';
import { MissionDialoguesService } from 'libs/missions/src/lib/mission-dialogues.service';
import { EMPTY, Subject, combineLatest, finalize } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import {
    OutstandingTasksViewTableSetupUserPreferences,
    WidgetRegistrationWithSection
} from '../../../outstanding-tasks-view.service';
import {
    MissionOutstandingStatusDataService,
    OutstandingStatusMission
} from '../mission-outstanding-status-data.service';

const WIDGET_IDENTIFIER = 'missionOutstandingStatusTaskWidget';

@Component({
    selector: 'missions-awaiting-finalisation',
    templateUrl: './missions-awaiting-finalisation.component.html',
    animations: [expandOnEnterAnimation(), collapseOnLeaveAnimation()],
    providers: [MissionOutstandingStatusDataService]
})
export class MissionsAwaitingFinalisationWidgetComponent
    implements OnInit, OnDestroy
{
    organisation: PersonsOrganisationDto;
    showBody = true;

    availableColumns: TableColumn[];
    selectedColumns: string[];
    tableConfig: TableConfig;
    gqlSorting: ColumnSortPreferences;

    missions: OutstandingStatusMission[] = [];
    userPreferences: OutstandingTasksViewTableSetupUserPreferences;

    working = false;
    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private missionOutstandingStatusDataService: MissionOutstandingStatusDataService,
        private workspaceStateService: WorkspaceStateService,
        private missionService: MissionService,
        private missionDialoguesService: MissionDialoguesService,
        private formatDateTimePipe: FormatDateTimePipe,
        private preferencesService: PreferencesService
    ) {
        this.availableColumns = [
            {
                value: 'name',
                name: 'Mission',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                linkFunction: row => this.viewMission(row)
            },
            {
                value: 'convertedMissionDate',
                name: 'Date',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                formatterFunction: t => this.formatDateTimePipe.transform(t)
            },
            {
                value: 'rpic',
                name: 'Pilot',
                searchable: false,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'rpaNicknames',
                name: 'RPA',
                searchable: false,
                selectable: true,
                sortable: false,
                defaultSelection: true
            },
            {
                value: 'locationName',
                name: 'Location',
                searchable: false,
                selectable: true,
                defaultSelection: false
            },
            {
                value: 'workflowName',
                name: 'Worklflow',
                searchable: false,
                selectable: true,
                defaultSelection: false
            }
        ];

        this.selectedColumns = ['rpaNicknames'];

        this.tableConfig = {
            serverPagination: true,
            currentPage: 0,
            limit: 3,
            actions: [],
            limitSelection: [3, 10, 25, 50, 100]
        };

        this.gqlSorting = null;

        this.missionOutstandingStatusDataService.working$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.loadPreferences();
            });

        combineLatest([
            this.missionOutstandingStatusDataService.missions$,
            this.missionOutstandingStatusDataService.currentPage$,
            this.missionOutstandingStatusDataService.totalItems$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([missions, currentPage, totalItems]) => {
                this.missions = missions;
                if (this.tableConfig != null) {
                    const currentConfig = this.tableConfig;
                    this.tableConfig = {
                        ...currentConfig,
                        currentPage: currentPage,
                        totalItems: totalItems
                    };
                }
            });

        this.missionService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshMissions());
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    onToggleCollapse() {
        this.showBody = !this.showBody;
        this.userPreferences = {
            ...this.userPreferences,
            showBody: this.showBody
        };
        this.updateUserPreferences();
    }

    private loadPreferences() {
        if (!this.organisation) {
            return;
        }
        this.preferencesService
            .findPreferences<OutstandingTasksViewTableSetupUserPreferences>(
                'missionsAwaitingFinalisationWidgetTable',
                this.organisation.id
            )
            .pipe(
                catchError(() => {
                    this.handleDefaultPreferences();
                    return EMPTY;
                }),
                finalize(() => {
                    this.refreshMissions();
                })
            )
            .subscribe(preferences => {
                if (preferences === null) {
                    this.handleDefaultPreferences();
                } else {
                    this.applyPreferences(preferences);
                }
            });
    }

    private handleDefaultPreferences() {
        this.tableConfig.limit = 3; // reset limit to default
        this.applyPreferences({});
    }

    private applyPreferences(
        preferences: OutstandingTasksViewTableSetupUserPreferences
    ) {
        this.userPreferences = applyPreferences(
            preferences,
            this.availableColumns,
            this.tableConfig
        );
        this.selectedColumns = this.userPreferences.selectedColumns;
        this.gqlSorting = this.userPreferences.columnSorting;
        this.tableConfig.limit = this.userPreferences.itemLimit;
        this.showBody = this.userPreferences.showBody ?? true;
    }

    updateUserPreferences() {
        this.preferencesService
            .updatePreferences(
                'missionsAwaitingFinalisationWidgetTable',
                this.organisation.id,
                this.userPreferences
            )
            .subscribe();
    }

    refreshMissions() {
        this.missionOutstandingStatusDataService.findMissions(
            this.tableConfig.currentPage ?? 0,
            this.tableConfig.limit ?? 3,
            this.gqlSorting,
            this.organisation.id,
            'finalisation',
            null,
            null
        );
    }

    viewMission(mission: OutstandingStatusMission) {
        this.missionDialoguesService.showMissionDetails(mission.id, true);
    }

    onTablePageChanged(page: number) {
        this.tableConfig.currentPage = page;
        this.refreshMissions();
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;
        if (
            this.gqlSorting != null &&
            !this.selectedColumns.includes(this.gqlSorting.column)
        ) {
            this.gqlSorting = null;
        }
        this.refreshMissions();
        this.userPreferences = {
            ...this.userPreferences,
            selectedColumns: selectedColumns,
            columnSorting: this.gqlSorting || null
        };
        this.updateUserPreferences();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.gqlSorting =
            sorting == null
                ? null
                : {
                      column: sorting.column.toString(),
                      ascending: sorting.ascending
                  };
        this.tableConfig.currentPage = 0;
        this.refreshMissions();
        this.userPreferences = {
            ...this.userPreferences,
            columnSorting: sorting
        };
        this.updateUserPreferences();
    }

    updateItemLimit(limit: number) {
        this.tableConfig.limit = limit;
        this.refreshMissions();
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        this.updateUserPreferences();
    }
}

export const missionsAwaitingFinalisationTaskWidgetRegistration: WidgetRegistrationWithSection =
    {
        widgetIdentifier: WIDGET_IDENTIFIER,
        section: 'operations',
        component: MissionsAwaitingFinalisationWidgetComponent,
        isAvailable: organisation =>
            hasFeatureFlag(organisation, FEATURE_OUTSTANDING_TASKS_VIEW),
        requiredPermissions: [
            PersonsOrganisationDto.Permissions.MISSION_APPROVE
        ]
    };
